/**
 * The reference submarine hull, decoded.
 *
 * `buildSub()` in models.ts derives the boat from measurements — a part table,
 * a superellipse revolve, stations along the hull. This one is the scanned
 * reference itself, cleaned up in Blender, decimated to about ten thousand
 * triangles and baked to a quantised blob by scripts/bake-sub.ts.
 *
 * BOTH ARE KEPT. Four attempts at deriving the reference's shape from numbers
 * got close and never got there — the compound curve along its top, the way the
 * saddle pods sit proud of the plating, the stepped sail — so this exists to be
 * compared against the built one rather than to replace it unseen. The renderer
 * registers whichever the player has selected, and nothing else in the game
 * knows the difference: same origin, same axis, same length, same MAT slots, so
 * every hardpoint, light and collision radius lands in the same place on both.
 */

import { MAT, type MeshData } from './mesh';
import {
  SUB_DATA, SUB_HULL_TRIS, SUB_HULL_VERTS, SUB_LAMP_PIVOT, SUB_LO, SUB_HI,
  SUB_TRIS, SUB_VERTS,
} from './subMesh';

/** 0 paint, 1 metal, 2 glow — the three slots the bake's colour sampling found. */
const SLOT = [MAT.paint, MAT.metal, MAT.glow];

/** Where the searchlight is mounted on the hull, in mesh-local metres. */
export const LAMP_PIVOT = SUB_LAMP_PIVOT;

let cached: { hull: MeshData; lamp: MeshData } | null = null;

/** The hull, without its searchlight. */
export function buildSubModel(): MeshData {
  return decode().hull;
}

/**
 * The searchlight alone, its mount at the origin and its bell pointing +X, so
 * the renderer can turn it to the beam without the hull following.
 */
export function buildSubLamp(): MeshData {
  return decode().lamp;
}

function decode(): { hull: MeshData; lamp: MeshData } {
  if (cached) return cached;

  // base64 -> bytes. `atob` is the one decoder present in both browsers and the
  // node harnesses that bundle this file, and at 133 KB it costs under a
  // millisecond, once, behind the loading screen.
  const raw = atob(SUB_DATA);
  const bin = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bin[i] = raw.charCodeAt(i);

  const nv = SUB_VERTS;
  const pos = new Int16Array(bin.buffer, 0, nv * 3);
  const nrm = new Int8Array(bin.buffer, nv * 6, nv * 3);
  const uvx = new Uint8Array(bin.buffer, nv * 9, nv);
  const mat = new Uint8Array(bin.buffer, nv * 10, nv);

  const sx = (SUB_HI[0] - SUB_LO[0]) / 65534;
  const sy = (SUB_HI[1] - SUB_LO[1]) / 65534;
  const sz = (SUB_HI[2] - SUB_LO[2]) / 65534;

  const vertices = new Float32Array(nv * 9);
  for (let k = 0; k < nv; k++) {
    const o = k * 9;
    vertices[o] = SUB_LO[0] + (pos[k * 3] + 32767) * sx;
    vertices[o + 1] = SUB_LO[1] + (pos[k * 3 + 1] + 32767) * sy;
    vertices[o + 2] = SUB_LO[2] + (pos[k * 3 + 2] + 32767) * sz;
    // Renormalised rather than merely scaled: quantising each component to
    // 1/127 leaves the vector slightly off unit length, and the shader's
    // specular term is sensitive enough to show it as faint mottling.
    const nx = nrm[k * 3] / 127, ny = nrm[k * 3 + 1] / 127, nz = nrm[k * 3 + 2] / 127;
    const l = Math.hypot(nx, ny, nz) || 1;
    vertices[o + 3] = nx / l;
    vertices[o + 4] = ny / l;
    vertices[o + 5] = nz / l;
    vertices[o + 6] = uvx[k] / 255;
    // uv.y is never read: `vsScene` replaces it with the material tag.
    vertices[o + 7] = 0;
    vertices[o + 8] = SLOT[mat[k]] ?? MAT.metal;
  }

  // Zigzag varint deltas. Written this way because the vertices arrive in
  // first-use order, so all but a handful of deltas fit in a single byte.
  const indices = new Uint32Array(SUB_TRIS * 3);
  let p = nv * 11;
  let prev = 0;
  for (let i = 0; i < indices.length; i++) {
    let shift = 0, z = 0, b = 0;
    do {
      b = bin[p++];
      z |= (b & 127) << shift;
      shift += 7;
    } while (b & 128);
    prev += (z >>> 1) ^ -(z & 1);
    indices[i] = prev;
  }

  // Split. The bake emits every hull part before every lamp part and records
  // the boundary, so this is two subarray views rather than any re-sorting —
  // and the lamp's indices are rebased onto its own vertex block.
  const hv = SUB_HULL_VERTS, ht = SUB_HULL_TRIS;
  const lampIdx = new Uint32Array(indices.length - ht * 3);
  for (let i = 0; i < lampIdx.length; i++) lampIdx[i] = indices[ht * 3 + i] - hv;

  cached = {
    hull: {
      vertices: vertices.subarray(0, hv * 9) as Float32Array<ArrayBuffer>,
      indices: indices.subarray(0, ht * 3) as Uint32Array<ArrayBuffer>,
    },
    lamp: {
      vertices: vertices.subarray(hv * 9) as Float32Array<ArrayBuffer>,
      indices: lampIdx,
    },
  };
  return cached;
}
